import React, { useRef, useEffect, useState } from "react";
import CommonPage from "@icarius-common/commonPage";
import EditableGridv2 from "@icarius-common/editableGrid/editableGridv2";
import CustomIconButton from "@icarius-common/abmComponents/customIconButton";
import AddRowStatusBar from '@icarius-common/editableGrid/components/addRow/addRowButton';
import paths from "@icarius-router/paths";
import { getLocalizedString } from "@icarius-localization/strings";
import { getGeneralValuesFromServer, setGeneralValuesToServer, deleteGeneralValuesInServer } from "../actions";
import { getRows, getLoading } from "../selectors";
import { useDispatch, useSelector } from "react-redux";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import ConfirmationDialog from "./confirmationDialog";
import {
  createDateFromYYYYMMDDHyphen,
  formatDateYYYYMMDD,
  formatDateYYYYMMDDHyphen,
  createDateFromDDMMYYYY,
} from "@icarius-utils/date";
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid } from "devextreme/excel_exporter"
import { openDialogAction } from "@icarius-common/dialog/actions";

const GeneralValues = ({ history }) => {

  const [dataChanged, setDataChanged] = useState(false);
  const [shouldReloadGrid, setShouldReloadGrid] = useState(false);
  const [confirmationDialogIsOpen, setConfirmationDialogIsOpen] = useState(false);
  const [gridReference, setGridReference] = useState(null);

  const dispatch = useDispatch();
  const rowData = useSelector(getRows);
  const isLoading = useSelector(getLoading);

  const gridRef = useRef();

  useEffect(() => {
    dispatch(getGeneralValuesFromServer());
    window.scrollTo(0, 0);
  }, [dispatch])

  useEffect(() => {
    if (gridRef) {
      setGridReference(gridRef.current);
    }
  }, [gridRef])

  const handleSave = () => {
    const rowData = gridReference.getVisibleRows().map(item => item.data);
    const formattedData = rowData.map((row) => {
      return {
        alfa: row.alfa,
        code: row.code,
        name: row.name,
        num: Boolean(!row.num || row.num === "") ? "" : parseFloat(row.num),
        date: Boolean(row.date) ? row.date instanceof Date ? formatDateYYYYMMDD(row.date) : formatDateYYYYMMDD(createDateFromYYYYMMDDHyphen(row.date)) : ''
      }
    })

    dispatch(setGeneralValuesToServer(formattedData))
      .then((resp) => {
        //limpiar el seleccionado
        gridReference.deselectAll()

        if (resp?.response?.data?.status === 'DUPLICATED_DATA') {
          const rowsToSelect = [];
          resp.response.data.fields.forEach(field => {
            const keysToSelect = resp.response.data[`${field}s`];
            gridReference.getVisibleRows().forEach(node => {
              if (keysToSelect.includes(node.data[field])) {
                rowsToSelect.push(node.key);
              }
            });

            gridReference.selectRows(rowsToSelect);
          })
        } else {
          setShouldReloadGrid(true);
        }

        if (resp?.status === 200) {
          setDataChanged(false);
        }
      })
  };

  const handleOpenDeleteDialog = () => {
    // Me fijo cuantas filas seleccionadas tengo
    let selectedRows = gridReference.getSelectedRowsData();

    // Si no hay seleccionado, pido seleccionar
    if (selectedRows.length < 1) {
      dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRow"), severity: "warning" }));
      return;
    }

    dispatch(openDialogAction({
      title: getLocalizedString("atention"),
      msg: getLocalizedString("doYouWantToDeleteSelectedRows"),
      onConfirm: () => deleteRowsFromServer(selectedRows),
    }));
  }

  const deleteRowsLocally = (rowsToDelete) => {
    gridReference.cancelEditData();
    if (Array.isArray(rowsToDelete)) {
      const ds = gridReference.getDataSource();
      const filter = gridReference.getCombinedFilter();
      ds.store().load({ sort: ds.sort(), filter: filter ? filter : null })
        .done((allData) => {
          const rowData = allData;
          rowsToDelete.forEach(row => {
            const filteredRows = rowData.filter(el => JSON.stringify(el) === JSON.stringify(row));
            filteredRows.forEach(gridRow =>
              gridReference.getDataSource().store().remove(gridRow).then(() => {
                gridReference.refresh();
              })
            )
          });
        });
    }
    dispatch(openSnackbarAction({ msg: getLocalizedString("valuesDeletedSuccessfully"), severity: "success" }));
  }

  const deleteRowsFromServer = (rowsToDelete) => {
    const codesToSend = rowsToDelete.filter(row => !row.hasOwnProperty("__KEY__")).map(row => row.code);

    if (codesToSend.length > 0) {
      dispatch(deleteGeneralValuesInServer(codesToSend))
        .then((resp) => {
          //limpiar el seleccionado
          gridReference.deselectAll();

          if (resp?.response?.data?.status === 'IN_USE') {
            const keysToSelect = resp.response.data.dependants;
            const rowsToSelect = [];
            gridReference.getVisibleRows().forEach(node => {
              if (keysToSelect.includes(node.data.code)) {
                rowsToSelect.push(node.key);
              }
            });

            gridReference.selectRows(rowsToSelect);
          } else {
            deleteRowsLocally(rowsToDelete);
          }
        })
    } else if (rowsToDelete.length > codesToSend.length) {
      deleteRowsLocally(rowsToDelete);
    }
  }

  const handleGoBack = () => {
    history.push(paths.ratingsAndGeneralValues);
  }

  const handleCheckGoBack = () => {
    if (dataChanged) {
      setConfirmationDialogIsOpen(true);
    } else {
      handleGoBack();
    }
  }

  const handleExportGrid = () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Valores generales');

    exportDataGrid({
      component: gridReference,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell.value) {
          if (gridCell.rowType === 'data') {
            if (gridCell.column.dataField === 'num' && gridCell.value !== "") {
              excelCell.value = parseFloat(gridCell.value);
              excelCell.numFmt = '_(#,##0.000_);_((#,##0.000)';
            }
          }
        }
      },
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Valores generales.xlsx');
      });
    });
  }

  const saveItem = () => (
    <CustomIconButton
      title={getLocalizedString("save")}
      onClick={handleSave}
      type={"save"}
    />
  )

  const deleteItem = () => (
    <CustomIconButton
      title={getLocalizedString("delete")}
      onClick={handleOpenDeleteDialog}
      type={"delete"}
    />
  )

  const addRowButton = () => (
    <AddRowStatusBar dataGridRef={gridRef} />
  )

  const goBackButton = () => (
    <CustomIconButton
      title={getLocalizedString("goBack")}
      onClick={handleCheckGoBack}
      type={"goBack"}
    />
  )

  const exportGrid = () => (
    <CustomIconButton
      title={getLocalizedString("export")}
      onClick={handleExportGrid}
      type={"excel"}
    />
  )
  const formattedData = rowData.map(row => { return { ...row, date: Boolean(row.date) ? formatDateYYYYMMDDHyphen(createDateFromDDMMYYYY(row.date)) : null } })

  useEffect(() => {
    rowData && setShouldReloadGrid(true);
  }, [rowData])

  return (
    <CommonPage
      title={getLocalizedString("generalValuesUppercase")}
      menuItems={[goBackButton, addRowButton, saveItem, exportGrid, deleteItem]}
      isLoading={isLoading}
      isNotGridPage
    >
      <EditableGridv2
        setShouldReloadGrid={setShouldReloadGrid}
        shouldReloadGrid={shouldReloadGrid}
        setDataChanged={setDataChanged}
        ref={gridRef}
        gridName="generalValue"
        rowData={formattedData}
        columns={getColumnDefByPage(paths.generalValues)}
      />
      {
        confirmationDialogIsOpen &&
        <ConfirmationDialog
          open={confirmationDialogIsOpen}
          handleConfirm={handleGoBack}
          handleClose={() => setConfirmationDialogIsOpen(false)}
        />
      }
    </CommonPage>
  );
}

export default GeneralValues;
